<template>
  <div class="w-full flex flex-col h-full">
    <!-- Main content -->
    <div class="w-full">
      <Tabs
        v-model:value="activeTab"
        class="w-full"
      >
        <TabList class="w-full flex border-b">
          <Tab value="overview">
            Overview
          </Tab>
          <Tab value="notes">
            Notes
          </Tab>
          <Tab value="documents">
            Documents
          </Tab>
          <Tab value="address">
            Address
          </Tab>
        </TabList>
        <TabPanels class="w-full py-6">
          <TabPanel
            value="overview"
            class="w-full"
          >
            <ContactOverviewTab
              :contact="contact"
              :is-editing="isEditing"
              @update:contact="updateContact"
            />
          </TabPanel>
          <TabPanel
            value="notes"
            class="w-full"
          >
            <ContactNotesTab
              :contact="contact"
              :is-editing="isEditing"
              @update:contact="updateContact"
            />
          </TabPanel>
          <TabPanel
            value="documents"
            class="w-full"
          >
            <ContactDocumentsTab
              :contact="contact"
              :is-editing="isEditing"
              @update:contact="updateContact"
            />
          </TabPanel>
          <TabPanel
            value="address"
            class="w-full"
          >
            <ContactAddressTab
              :contact="contact"
              :is-editing="isEditing"
              @update:contact="updateContact"
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>

    <!-- Footer with action buttons -->
    <div class="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-200">
      <div
        v-if="type === 'view' && !isEditing"
        class="flex gap-2"
      >
        <Button
          label="Edit"
          @click="startEdit"
        />
        <Button
          label="Delete"
          class="p-button-danger"
          @click="triggerDelete"
        />
      </div>

      <div
        v-if="isEditing"
        class="flex gap-2"
      >
        <Button
          v-if="type === 'add'"
          label="Save"
          :disabled="isSaving"
          :loading="isSaving"
          @click="saveChanges"
        />
        <Button
          v-if="type === 'view'"
          label="Save"
          :disabled="isSaving"
          :loading="isSaving"
          @click="updateItem"
        />
        <Button
          label="Cancel"
          class="p-button-secondary"
          :disabled="isSaving"
          @click="cancelEdit"
        />
      </div>
    </div>
    <Toast />
    <ConfirmDialog />
  </div>
</template>

<script setup>
import { ref, watch, onMounted, provide } from "vue";
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { useContactStore } from '@/stores/contactsStore';
import { uploadContactImage } from '@/services/contactService';
import { mapContactToBackend, mapContactFromBackend } from '@/utils/contactFieldMapper';
import ContactOverviewTab from '@/components/contacts/view/ContactOverviewTab.vue';
import ContactNotesTab from '@/components/contacts/view/ContactNotesTab.vue';
import ContactDocumentsTab from '@/components/contacts/view/ContactDocumentsTab.vue';
import ContactAddressTab from '@/components/contacts/view/ContactAddressTab.vue';
import defaultImage from "@/assets/icons/Profile.svg";

const props = defineProps({
    data: { type: Object, default: () => ({}) },
    type: { type: String, default: 'view' },
});

const emit = defineEmits(['contact-created']);
const router = useRouter();
const contactStore = useContactStore();
const toast = useToast();
const confirm = useConfirm();

const contact = ref({ ...props.data });
const isEditing = ref(false);
const isSaving = ref(false);
const imageUploadFile = ref(null);
const activeTab = ref('overview');

// Form actions
const startEdit = () => {
    isEditing.value = true;
};

const cancelEdit = () => {
    isEditing.value = false;
    // Reset contact data to original props
    if (props.type === 'add') {
        contact.value = {
            name: "",
            jobTitle: "",
            number: "",
            email: "",
            company: "",
            description: "",
            isBookable: false,
            isVat: false,
            image: defaultImage,
            notes: "",
            documents: [],
            address: {
        address1: "",
        address2: "",
        town: "",
        county: "",
        postcode: "",
        country: ""
            }
        };
        imageUploadFile.value = null;
    } else {
        contact.value = { ...props.data };
    }
};

const updateContact = (updatedContact) => {
    Object.assign(contact.value, updatedContact);
};

const saveChanges = async () => {
    if (isSaving.value) return;
    
    if (!contact.value.name || contact.value.name.trim() === '') {
        toast.add({
            severity: 'warn',
            summary: 'Validation Error',
            detail: 'Name is required',
            life: 5000
        });
        return;
    }

    isSaving.value = true;
    try {
        // Map frontend format to backend format
        const backendData = mapContactToBackend(contact.value);
        
        // Create contact
        const response = await contactStore.createContact(backendData);
        // Backend returns: { success: true, message: "...", data: { id, ... } }
        const newContactId = response.data?.id || response.id;
        
        if (!newContactId) {
            throw new Error('Contact created but no ID returned');
        }
        
        // Upload image if one was selected
        if (imageUploadFile.value) {
            try {
                await uploadContactImage(newContactId, imageUploadFile.value);
            } catch (imageError) {
                console.error('Error uploading image:', imageError);
                toast.add({
                    severity: 'warn',
                    summary: 'Image Upload Failed',
                    detail: 'Contact created but image upload failed. You can upload it later.',
                    life: 7000
                });
            }
        }
        
        toast.add({
            severity: 'success',
            summary: 'Contact Created',
            detail: 'Contact created successfully!',
            life: 5000
        });
        
        // Emit event to parent to close dialog
        emit('contact-created', response);
    } catch (error) {
        console.error('Error creating contact:', error);
        const errorMessage = error.response?.data?.message || 
                            (error.response?.data?.errors ? 
                                Object.values(error.response.data.errors).flat().join(', ') : 
                                'Failed to create contact');
        toast.add({
            severity: 'error',
            summary: 'Creation Failed',
            detail: errorMessage,
            life: 7000
        });
    } finally {
        isSaving.value = false;
    }
};

const updateItem = async () => {
    if (isSaving.value) return;
    
    if (!contact.value.name || contact.value.name.trim() === '') {
        toast.add({
            severity: 'warn',
            summary: 'Validation Error',
            detail: 'Name is required',
            life: 5000
        });
        return;
    }

    isSaving.value = true;
    try {
        // Map frontend format to backend format
        const backendData = mapContactToBackend(contact.value);
        
        // Update contact
        const response = await contactStore.updateContact({
            id: contact.value.id,
            ...backendData
        });
        
        // Upload image if one was selected
        if (imageUploadFile.value) {
            try {
                await uploadContactImage(contact.value.id, imageUploadFile.value);
                // Reload contact to get updated image URL
                const updatedContact = await contactStore.getContactById(contact.value.id);
                contact.value = mapContactFromBackend(updatedContact);
            } catch (imageError) {
                console.error('Error uploading image:', imageError);
                toast.add({
                    severity: 'warn',
                    summary: 'Image Upload Failed',
                    detail: 'Contact updated but image upload failed. You can try again later.',
                    life: 7000
                });
                }
            } else {
            // Map response back to frontend format
            contact.value = mapContactFromBackend(response);
        }
        
        toast.add({
            severity: 'success',
            summary: 'Contact Updated',
            detail: 'Contact updated successfully!',
            life: 5000
        });
        
        isEditing.value = false;
        imageUploadFile.value = null;
    } catch (error) {
        console.error('Error updating contact:', error);
        const errorMessage = error.response?.data?.message || 
                            (error.response?.data?.errors ? 
                                Object.values(error.response.data.errors).flat().join(', ') : 
                                'Failed to update contact');
        toast.add({
            severity: 'error',
            summary: 'Update Failed',
            detail: errorMessage,
            life: 7000
        });
    } finally {
        isSaving.value = false;
    }
};

const triggerDelete = () => {
    confirm.require({
        message: 'Are you sure you want to delete this contact? This action cannot be undone.',
        header: 'Confirm Delete',
        icon: 'pi pi-exclamation-triangle',
        rejectClass: 'p-button-secondary p-button-outlined',
        rejectLabel: 'Cancel',
        acceptLabel: 'Delete',
        accept: async () => {
            try {
                await contactStore.deleteContact(contact.value.id);
                toast.add({
                    severity: 'success',
                    summary: 'Contact Deleted',
                    detail: 'Contact deleted successfully',
                    life: 5000
                });
                router.push('/contacts');
            } catch (error) {
                console.error('Error deleting contact:', error);
                const errorMessage = error.response?.data?.message || 'Failed to delete contact';
                toast.add({
                    severity: 'error',
                    summary: 'Delete Failed',
                    detail: errorMessage,
                    life: 7000
                });
            }
        }
    });
};

// Handle image upload from child components (for add mode)
const handleImageUpload = (file) => {
    imageUploadFile.value = file;
    
    // Preview image immediately
    const reader = new FileReader();
    reader.onload = () => {
        contact.value.image = reader.result;
    };
    reader.readAsDataURL(file);
};

// Expose image upload handler to child components via provide/inject
provide('handleImageUpload', handleImageUpload);
provide('imageUploadFile', imageUploadFile);

// Initialize form on mount
onMounted(() => {
    isEditing.value = props.type === 'add';

    if (props.type === 'add') {
        // Initialize with empty data for a new contact
        contact.value = {
            name: "",
            jobTitle: "",
            number: "",
            email: "",
            company: "",
            description: "",
            isBookable: false,
            isVat: false,
            image: defaultImage,
            notes: "",
            documents: [],
            address: {
                address1: "",
                address2: "",
                town: "",
                county: "",
                postcode: "",
                country: ""
            }
        };
    } else if (props.type === 'view' && props.data && props.data.id) {
        // Map backend format to frontend format if data is provided
        contact.value = mapContactFromBackend(props.data);
    }
});

// Watch for changes in the type prop to adjust the form state
watch(
    () => props.type,
    (newType) => {
        isEditing.value = newType === "add";
    }
);
</script>

<style scoped>
/* Ensure components take full width */
:deep(.p-tabview-panels) {
    width: 100% !important;
}

:deep(.p-tabview-panel) {
    width: 100% !important;
}

:deep(.p-datatable) {
    width: 100% !important;
}
</style>
